import React from 'react';
import { TEST_IMPLICATIONS } from '../data/regionalConfig.js';
import { GLIDE_DIRECTIONS, FUNCTIONAL_TESTS, NERVE_ROOTS, IMAGING_MODALITIES } from '../data/clinicalOptions.js';
import { theme, styles } from '../constants.js';

const SmartRow = ({ mode, data, onChange, onDelete, config }) => {
  const getTestLogic = (testName, result) => (result === 'Positive' && TEST_IMPLICATIONS[testName]) ? TEST_IMPLICATIONS[testName] : null;
  const deleteBtn = <button type="button" onClick={onDelete} aria-label="Delete row" style={{background:'none', border:'none', color:'#fca5a5', fontSize:'1.4rem', cursor:'pointer', transition: 'color 0.15s', minWidth:'44px', minHeight:'44px', display:'inline-flex', alignItems:'center', justifyContent:'center', flexShrink:0, borderRadius:'8px'}} onMouseEnter={(e)=>e.currentTarget.style.color=theme.danger} onMouseLeave={(e)=>e.currentTarget.style.color='#fca5a5'}>×</button>;

  if (mode === 'ROM') {
    const hasPain = !!data.pain;
    return (
      <div className="sr-rom-row" style={{
        background: hasPain ? '#fff8f8' : '#fff',
        border: `1.5px solid ${hasPain ? '#fca5a5' : theme.border}`,
        borderRadius: 14, padding: '10px 12px 10px 14px', marginBottom: 10,
        transition: 'border-color 0.15s, background 0.15s',
      }}>

        {/* Row 1 — Movement (always full width) */}
        <select
          value={data.move}
          onChange={e => onChange({ move: e.target.value })}
          style={{ width: '100%', padding: '8px 10px', borderRadius: 9, border: `1.5px solid ${theme.border}`, fontSize: '1rem', fontWeight: 600, color: data.move ? theme.textMain : '#94a3b8', background: '#f8fafc', outline: 'none', cursor: 'pointer', fontFamily: 'inherit', marginBottom: 8 }}
        >
          <option value="">Movement...</option>
          {config?.movements?.map(m => <option key={m} value={m}>{m}</option>)}
        </select>

        {/* Row 2 — ROM values + controls */}
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>

          {/* Active ROM */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
            <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', whiteSpace: 'nowrap' }}>Active</span>
            <input
              type="number" min="0" max="360" placeholder="—"
              value={data.activeRom ?? ''}
              onChange={e => onChange({ activeRom: e.target.value })}
              style={{ width: 48, minWidth: 44, padding: '8px 4px', borderRadius: 9, border: `1.5px solid ${theme.border}`, fontSize: '0.95rem', fontWeight: 700, color: theme.textMain, background: '#f8fafc', outline: 'none', textAlign: 'center', fontFamily: 'inherit' }}
            />
            <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#94a3b8' }}>°</span>
          </div>

          {/* Passive ROM */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
            <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', whiteSpace: 'nowrap' }}>Passive</span>
            <input
              type="number" min="0" max="360" placeholder="—"
              value={data.passiveRom ?? ''}
              onChange={e => onChange({ passiveRom: e.target.value })}
              style={{ width: 48, minWidth: 44, padding: '8px 4px', borderRadius: 9, border: `1.5px solid ${theme.border}`, fontSize: '0.95rem', fontWeight: 700, color: theme.textMain, background: '#f8fafc', outline: 'none', textAlign: 'center', fontFamily: 'inherit' }}
            />
            <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#94a3b8' }}>°</span>
          </div>

          {/* Pain toggle */}
          <button
            type="button"
            onClick={() => onChange({ pain: !data.pain })}
            style={{
              padding: '7px 13px', borderRadius: 99, cursor: 'pointer', fontWeight: 700, fontSize: '0.75rem',
              border: `1.5px solid ${hasPain ? '#ef4444' : '#e2e8f0'}`,
              background: hasPain ? '#fef2f2' : '#f8fafc',
              color: hasPain ? '#ef4444' : '#94a3b8',
              whiteSpace: 'nowrap', transition: 'all 0.15s', flexShrink: 0,
            }}
          >
            {hasPain ? '⚡ Painful' : 'Pain-free'}
          </button>

          {/* End Feel */}
          <select
            value={data.endFeel || ''}
            onChange={e => onChange({ endFeel: e.target.value })}
            style={{ flex: '1 1 100px', minWidth: 90, padding: '8px 10px', borderRadius: 9, border: `1.5px solid ${theme.border}`, fontSize: '1rem', fontWeight: 600, color: data.endFeel ? theme.textMain : '#94a3b8', background: '#f8fafc', outline: 'none', cursor: 'pointer', fontFamily: 'inherit' }}
          >
            <option value="">End feel…</option>
            <option>Soft</option><option>Firm</option><option>Hard</option>
            <option>Empty</option><option>Spasm</option>
          </select>

          {deleteBtn}
        </div>
      </div>
    );
  }

  if (mode === 'MMT') {
    return (
      <div style={{...styles.dynamicRow, backgroundColor: '#faf5ff', borderColor: '#e9d5ff'}}>
        <div style={{...styles.inputGroup, flex: 1.5}}><label style={styles.label}>Resisted Action</label>
          <select style={styles.input} value={data.move} onChange={(e) => onChange({ move: e.target.value })}><option value="">Select...</option>{config?.movements?.map(m => <option key={m} value={m}>{m}</option>)}</select>
        </div>
        <div style={{...styles.inputGroup, flex: 2}}><label style={styles.label}>Oxford Grade</label>
          <div style={styles.chipRow}>
            {['5', '4', '3', '2', '1', '0'].map(num => <button key={num} type="button" style={{...styles.chip, padding: '8px 12px', ...(data.grade === num ? styles.chipPurple : {})}} onClick={() => onChange({ grade: num })}>G{num}</button>)}
          </div>
        </div>
        {deleteBtn}
      </div>
    );
  }

  if (mode === 'GLIDE') {
    const glides = config?.glideDirections || GLIDE_DIRECTIONS;
    return (
      <div style={{...styles.dynamicRow, backgroundColor: '#fffbeb', borderColor: '#fde68a'}}>
        <div style={styles.inputGroup}><label style={styles.label}>Joint Glide Direction</label>
          <select style={styles.input} value={data.direction} onChange={(e) => onChange({ direction: e.target.value })}><option value="">Select...</option>{glides.map(m => <option key={m} value={m}>{m}</option>)}</select>
        </div>
        <div style={styles.inputGroup}><label style={styles.label}>Mobility</label>
          <div style={styles.chipRow}>
            {['Hypomobile', 'Normal', 'Hypermobile'].map(opt => <button key={opt} type="button" style={{...styles.chip, padding: '8px 12px', ...(data.mobility === opt ? styles.chipWarning : {})}} onClick={() => onChange({ mobility: opt })}>{opt}</button>)}
          </div>
        </div>
        <div style={styles.inputGroup}><label style={styles.label}>Symptom</label>
          <div style={styles.chipRow}>
             <button type="button" style={{...styles.chip, padding: '8px 12px', ...(data.symptom === 'Painful' ? styles.chipDanger : {})}} onClick={() => onChange({ symptom: 'Painful' })}>Painful</button>
             <button type="button" style={{...styles.chip, padding: '8px 12px', ...(data.symptom === 'Pain-Free' ? styles.chipSuccess : {})}} onClick={() => onChange({ symptom: 'Pain-Free' })}>Pain-Free</button>
          </div>
        </div>
        {deleteBtn}
      </div>
    );
  }

  if (mode === 'NEURO') {
    const roots = config?.neuroRoots || NERVE_ROOTS;
    return (
      <div style={{...styles.dynamicRow, backgroundColor: '#fdf4ff', borderColor: '#fbcfe8'}}>
        <div style={{...styles.inputGroup, flex: 1.5}}><label style={styles.label}>Root / Map</label>
          <select style={styles.input} value={data.root} onChange={(e) => onChange({ root: e.target.value })}><option value="">Select...</option>{roots.map(t => <option key={t} value={t}>{t}</option>)}</select>
        </div>
        <div style={{...styles.inputGroup, flex: 1.5}}><label style={styles.label}>Dermatome</label>
          <select style={styles.input} value={data.dermatome} onChange={(e) => onChange({ dermatome: e.target.value })}><option value="">Normal</option><option value="Hypoesthesia">Hypoesthesia</option><option value="Hyperesthesia">Hyperesthesia</option><option value="Absent">Absent</option></select>
        </div>
        <div style={{...styles.inputGroup, flex: 1}}><label style={styles.label}>Myotome</label>
          <select style={styles.input} value={data.myotome} onChange={(e) => onChange({ myotome: e.target.value })}><option value="">Grade</option><option value="5">5</option><option value="4">4</option><option value="3">3</option><option value="2">2</option><option value="1">1</option><option value="0">0</option></select>
        </div>
        <div style={{...styles.inputGroup, flex: 1}}><label style={styles.label}>Reflex</label>
          <select style={styles.input} value={data.reflex} onChange={(e) => onChange({ reflex: e.target.value })}><option value="">Scale</option><option value="0">0</option><option value="1+">1+</option><option value="2+">2+</option><option value="3+">3+</option><option value="4+">4+</option></select>
        </div>
        {deleteBtn}
      </div>
    );
  }

  if (mode === 'FUNCTIONAL') {
    const funcs = config?.functionalTests || FUNCTIONAL_TESTS;
    return (
      <div style={{...styles.dynamicRow, backgroundColor: '#f0fdf4', borderColor: '#6ee7b7'}}>
        <div style={{...styles.inputGroup, flex: 1.5}}><label style={styles.label}>Performance Test</label>
          <select style={styles.input} value={data.test} onChange={(e) => onChange({ test: e.target.value })}><option value="">Select...</option>{funcs.map(t => <option key={t} value={t}>{t}</option>)}</select>
        </div>
        <div style={{...styles.inputGroup, flex: 2}}><label style={styles.label}>Result / Score</label>
          <input type="text" style={styles.input} placeholder="e.g. 12 seconds, Unable, 5 reps" value={data.result} onChange={(e) => onChange({ result: e.target.value })} />
        </div>
        {deleteBtn}
      </div>
    );
  }

  if (mode === 'SPECIAL') {
    const hasHint = data.test && TEST_IMPLICATIONS[data.test];
    return (
      <div style={{...styles.dynamicRow, backgroundColor: '#f0f9ff', borderColor: '#bae6fd'}}>
        <div style={{...styles.inputGroup, flex: 2}}><label style={styles.label}>Test Name</label>
          <select style={{...styles.input, backgroundColor:'#fff'}} value={data.test} onChange={(e) => onChange({ test: e.target.value })}><option value="">Search...</option>{config?.specialTests?.map(t => <option key={t} value={t}>{t}</option>)}</select>
          {hasHint && !data.result && <span style={{fontSize: '0.85rem', color: theme.textMuted, marginTop: '6px', fontWeight: '500'}}>💡 Checks for: {TEST_IMPLICATIONS[data.test]}</span>}
        </div>
        <div style={{...styles.inputGroup, flex: 1}}><label style={styles.label}>Result</label>
          <div style={styles.chipRow}>
             <button type="button" style={{...styles.chip, padding: '8px 14px', ...(data.result === 'Positive' ? styles.chipDanger : {})}} onClick={() => onChange({ result: 'Positive' })}>Positive (+)</button>
             <button type="button" style={{...styles.chip, padding: '8px 14px', ...(data.result === 'Negative' ? styles.chipSuccess : {})}} onClick={() => onChange({ result: 'Negative' })}>Negative (-)</button>
          </div>
        </div>
        {getTestLogic(data.test, data.result) && <div style={styles.logicHint}>🎯 <strong>Indicates:</strong> {getTestLogic(data.test, data.result)}</div>}
        {deleteBtn}
      </div>
    );
  }

  if (mode === 'INVESTIGATION') {
    return (
      <div style={{...styles.dynamicRow, backgroundColor: '#eef2ff', borderColor: '#c7d2fe'}}>
        <div style={{...styles.inputGroup, flex: 1}}><label style={styles.label}>Modality</label>
          <select style={styles.input} value={data.type} onChange={(e) => onChange({ type: e.target.value })}><option value="">Select...</option>{IMAGING_MODALITIES.map(m => <option key={m} value={m}>{m}</option>)}</select>
        </div>
        <div style={{...styles.inputGroup, flex: 2}}><label style={styles.label}>Radiological / Lab Findings</label>
          <input type="text" style={styles.input} placeholder="e.g. Mild OA changes, C5-C6 disc bulge..." value={data.finding} onChange={(e) => onChange({ finding: e.target.value })} />
        </div>
        {deleteBtn}
      </div>
    );
  }

  return null;
};

export default SmartRow;
